/**
 * StageGatesPage — admin editor for stage-artifact gates.
 *
 * Step 4 of the workflow rollout. Lists workspace-default rows from
 * /stage-artifact-requirements; admins can add, edit, disable, or
 * delete each (target_stage, required_artifacts[]) row. The transition
 * endpoint (routers/bids._resolve_required_artifacts) reads from this
 * table first, falls back to the hard-coded defaults when no row
 * exists.
 *
 * Per-project overrides ship in step 6 via the project settings page
 * (this page only exposes the workspace tier).
 */
import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Plus, Trash2, Edit3, Save, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

// Mirror BidStage in backend/app/models/bid.py. Showing just the
// 6 we typically gate; admin can type any value (validated server-side).
const COMMON_STAGES = [
  { code: "BID_PREPARATION",    label: "Preparing Bid" },
  { code: "BID_SUBMITTED",      label: "Submitted" },
  { code: "CONTRACT_GENERATED", label: "Contract Generated" },
  { code: "ORDER_PLACED",       label: "Order Placed" },
  { code: "CRAC_ISSUED",        label: "CRAC Issued" },
  { code: "BILL_GENERATED",     label: "Bill Generated" },
];

const ARTIFACT_OPTIONS = [
  "bid_matrix", "quotation", "proposal", "compliance",
  "design", "maf", "datasheet", "submission_package",
];

const stageLabel = (code) =>
  COMMON_STAGES.find((s) => s.code === code)?.label || code;


/**
 * Reusable editor body. Mount it standalone at /v2/stage-gates
 * (workspace tier) or inside a project page (project tier). When
 * projectId is set, the API automatically attaches it to created
 * rows and the list call returns workspace + project rows merged.
 */
export function StageGatesEditor({ projectId = null, compact = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);    // row id OR "new"
  const [draft, setDraft] = useState({ target_stage: "", required_artifacts: [], is_active: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.stageArtifactRequirements.list(projectId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({
      target_stage: r.target_stage,
      required_artifacts: r.required_artifacts || [],
      is_active: r.is_active !== false,
    });
  };

  const startNew = () => {
    setEditing("new");
    setDraft({ target_stage: "", required_artifacts: [], is_active: true });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({ target_stage: "", required_artifacts: [], is_active: true });
  };

  const toggleArtifact = (k) => {
    setDraft((d) => ({
      ...d,
      required_artifacts: d.required_artifacts.includes(k)
        ? d.required_artifacts.filter((x) => x !== k)
        : [...d.required_artifacts, k],
    }));
  };

  const save = async () => {
    if (!draft.target_stage.trim()) {
      toast.error("target_stage is required.");
      return;
    }
    setBusy(true);
    try {
      if (editing === "new") {
        await v2Api.stageArtifactRequirements.create({
          target_stage: draft.target_stage.trim().toUpperCase(),
          required_artifacts: draft.required_artifacts,
          is_active: draft.is_active,
          ...(projectId ? { project_id: projectId } : {}),
        });
        toast.success(`Created gate for ${stageLabel(draft.target_stage)}`);
      } else {
        await v2Api.stageArtifactRequirements.patch(editing, {
          required_artifacts: draft.required_artifacts,
          is_active: draft.is_active,
        });
        toast.success("Saved");
      }
      cancelEdit();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Save failed"));
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r) => {
    setBusy(true);
    try {
      await v2Api.stageArtifactRequirements.patch(r.id, { is_active: !r.is_active });
      await load();
    } catch (e) {
      toast.error("Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!await confirm(`Delete the gate for ${stageLabel(r.target_stage)}?`, { title: "Delete stage gate", confirmLabel: "Delete" })) return;
    setBusy(true);
    try {
      await v2Api.stageArtifactRequirements.remove(r.id);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const editor = (
    <div className="border rounded-md p-3 bg-blue-50/30 space-y-3">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
          Target stage
        </label>
        <Input
          placeholder="BID_SUBMITTED"
          value={draft.target_stage}
          onChange={(e) => setDraft({ ...draft, target_stage: e.target.value.toUpperCase() })}
          disabled={editing !== "new"}
          className="text-xs font-mono mt-1"
        />
        <div className="flex flex-wrap gap-1 mt-1">
          {COMMON_STAGES.map((s) => (
            <button key={s.code} type="button"
              onClick={() => setDraft({ ...draft, target_stage: s.code })}
              disabled={editing !== "new"}
              className="text-[10px] px-2 py-0.5 border rounded text-slate-600 hover:bg-slate-100 disabled:opacity-50">
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
          Required artifacts (all must be approved before this transition)
        </label>
        <div className="flex flex-wrap gap-1 mt-1">
          {ARTIFACT_OPTIONS.map((a) => {
            const on = draft.required_artifacts.includes(a);
            return (
              <button key={a} type="button" onClick={() => toggleArtifact(a)}
                className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}>
                {on ? "✓ " : ""}{a}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <label className="text-xs flex items-center gap-1.5">
          <input type="checkbox" checked={draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
          Active
        </label>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={busy}>
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Card className="border-[#E2E8F0]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">
              {projectId ? "Project stage gates" : "Workspace defaults"}
            </CardTitle>
            <CardDescription>
              {projectId
                ? "Per-project overrides. A row here wins over the workspace default for the same target_stage."
                : "Apply to every project unless overridden. Per-project overrides ship in step 6 via project settings."}
            </CardDescription>
          </div>
          {editing === null && (
            <Button size="sm" onClick={startNew}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add gate
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && (
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        )}
        {error && (
          <p className="text-sm text-rose-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </p>
        )}
        {editing === "new" && editor}
        {!loading && !error && rows.length === 0 && editing !== "new" && (
          <p className="text-sm text-slate-400 italic">
            No gates defined yet. {projectId
              ? "This project will use workspace defaults until you add one here."
              : "The hard-coded fallback applies until you add a workspace row."}
          </p>
        )}
        {rows.map((r) => (
          editing === r.id ? <div key={r.id}>{editor}</div> : (
            <div key={r.id}
              className={`border rounded-md p-3 ${r.is_active ? "bg-white" : "bg-slate-50 opacity-60"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-xs text-slate-500">{r.target_stage}</p>
                    <p className="text-sm font-medium text-slate-800">{stageLabel(r.target_stage)}</p>
                    <Badge variant="outline" className="text-[10px]">
                      {r.project_id ? "Project override" : "Workspace default"}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(r.required_artifacts || []).length === 0 ? (
                      <span className="text-[10px] text-slate-400 italic">no artifacts required</span>
                    ) : (
                      r.required_artifacts.map((a, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] bg-slate-100">
                          {a}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Disable / Enable / Edit / Delete only apply to rows the
                      editor "owns" — workspace-default rows can't be deleted
                      from a project-tier editor, but they can be overridden
                      by adding a project row with the same target_stage. */}
                  {(!projectId || r.project_id) && (
                    <>
                      <button type="button" onClick={() => toggle(r)}
                        className="text-[11px] px-2 py-1 rounded border text-slate-600 hover:bg-slate-100"
                        disabled={busy}>
                        {r.is_active ? "Disable" : "Enable"}
                      </button>
                      <button type="button" onClick={() => startEdit(r)}
                        className="text-slate-500 hover:text-blue-600" disabled={busy}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(r)}
                        className="text-slate-500 hover:text-rose-600" disabled={busy}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {projectId && !r.project_id && (
                    <button type="button" onClick={() => startEdit({...r, id: "new"})}
                      className="text-[11px] px-2 py-1 rounded border text-slate-600 hover:bg-slate-100"
                      title="Create a project-tier override with the same target_stage"
                      disabled={busy}>
                      Override
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        ))}
      </CardContent>
    </Card>
  );
}


/**
 * Standalone /v2/stage-gates page — wraps StageGatesEditor in workspace
 * mode with a sticky header.
 */
export default function StageGatesPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          Stage gates
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Define which artifacts must be marked "approved" on a bid before
          each stage transition is allowed. The transition endpoint reads
          from this table first and falls back to the hard-coded defaults
          when no row exists.
        </p>
      </div>
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <StageGatesEditor projectId={null} />
      </div>
    </div>
  );
}
